import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_SEARCHABLE_DROPDOWN,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';
import useAppContext from '/business/shared/hooks/useAppContext';
import StyledBuildingRefrigerantsSchemaHint from './__styled__/StyledBuildingRefrigerantsSchema';

type RefrigerantType = {
  name: string | null;
  units?: Array<number | string>;
};

export default function useBuildingRefrigerantsSchema({
  refrigerantTypes,
}: {
  answerHasNoData?: boolean;
  hasAnswer?: boolean;
  refrigerantTypes: RefrigerantType[];
}) {
  const { t } = useTranslation();
  const { questionsPage } = useAppContext();
  const [valueRefrigerantType, setValueRefrigerantType] = useState<string | null>(null);
  const [forceSubmitForm, setForceSubmitForm] = useState(false);

  const validationValue = questionsPage?.hasCustomValidationValues
    ? (questionsPage.validationValues?.refrigerants ?? 99999)
    : 99999;

  const refrigerantType = useMemo(() => {
    return {
      type: FIELD_TYPE_SEARCHABLE_DROPDOWN,
      element: 'refrigerant_type',
      propName: 'refrigerantType',
      label: 'Emission source:',
      placeholderI18nKey: 'business:QuestionPage.forms.refrigerants.refrigerantPlaceholder',
      dropdownTitleI18nKey: 'business:QuestionPage.forms.refrigerants.dropdownTitle',
      saveMeCallback: (value: { inputValue?: string | null }) => {
        const { inputValue: refrigerantTypeSlug } = value;
        return {
          refrigerantType: refrigerantTypes.find(({ name }) => name === refrigerantTypeSlug),
          hasRefrigerantType: true,
        };
      },
      values: refrigerantTypes,
      styleInputParentWidth: '20rem',
      onChangeCallback: (value: string | null) => {
        setValueRefrigerantType(value);
      },
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELTYPE',
          errStr: t('business:Errors.refrigerant.provideType'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRFUELTYPENOTFOUND',
          errStr: t('business:Errors.refrigerant.notFound'),
        },
      ],
    };
  }, [refrigerantTypes, t]);

  const refrigerantTypeHint = useMemo(() => {
    return (
      <StyledBuildingRefrigerantsSchemaHint $smallText $grey $hasExtraMarginBottom>
        {/* @i18n: Can't find your refrigerant type or unsure which to use? It should be listed on your[...] */}
        <Trans i18nKey="business:QuestionPage.forms.refrigerants.para4" />
      </StyledBuildingRefrigerantsSchemaHint>
    );
  }, []);

  const quantity = useMemo(() => {
    const refrigerantUnits = refrigerantTypes.map((item) => {
      const typeUnitMapped = item?.units?.map((typeUnit) => {
        const unitName = String(typeUnit);
        const transLitDisplay = t('business:emissionFactors.volumes.'.concat(unitName), unitName);
        return {
          name: unitName,
          displayName: transLitDisplay,
        };
      });
      return {
        ...item,
        units: typeUnitMapped,
      };
    });
    return {
      element: 'quantity',
      propName: 'quantity',
      type: FIELD_TYPE_NUMBER,
      placeholderI18nKey: 'business:QuestionPage.forms.refrigerants.quantityPlaceholder',
      dropdown: {
        element: 'refrigerant_unit',
        values: refrigerantUnits?.find(({ name }) => name === valueRefrigerantType)?.units,
        propName: 'refrigerantUnit',
      },
      label: 'Quantity:',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOQUANTITY',
          errStr: t('business:Errors.refrigerant.provideQty'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRQUANTITYTOOLOW',
          errStr: t('business:Errors.refrigerant.qtyTooLow'),
          value: 0,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRQUANTITYTOOHIGH',
          errStr: t('business:Errors.refrigerant.qtyTooHigh'),
          value: validationValue,
        },
      ],
    };
  }, [validationValue, refrigerantTypes, valueRefrigerantType, t]);

  const dataSource = useMemo(() => {
    return {
      type: FIELD_TYPE_TXTAREA,
      propName: 'dataSource',
      element: 'data_source',
      labelI18nKey: 'business:QuestionPage.forms.refrigerants.para6',
      description: <></>,
      placeholderI18nKey: 'business:QuestionPage.forms.refrigerants.dataPlaceholder',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.tooShort'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.tooLong'),
          value: 3000,
        },
      ],
    };
  }, [t]);

  const pageSchema = useMemo(
    () => [refrigerantType, refrigerantTypeHint, quantity, dataSource],
    [refrigerantType, refrigerantTypeHint, quantity, dataSource],
  );
  return {
    pageSchema,
    forceSubmitForm,
    setForceSubmitForm,
  };
}
